import React from 'react';
import BaseForm from '../../../../../components/form/BaseForm';
import InputForm from '../../../../../components/form/InputForm';
import ImgUpload from '../../../../../components/ImgUpload';
import Anchor from '../../../../../components/link/Anchor';
import Panel from '../../../../../components/outline/Panel';
import SearchAddress from '../../../../../components/SearchAddress';
import ConfirmTag from '../../../../../components/tag/ConfirmTag';
import {
  MAIN_ADDRESS_LINE2_MAX_LENGTH,
  MAXIMUM_NUMBER_OF_STORE_CONCEPT_TAG,
  STORE_STATUS,
} from '../../../../../constants/service/app/brandConstants';
import { TAG_GROUP_KIND } from '../../../../../constants/service/app/tagConstants';
import { setClearDash, setDash, setPhoneFormat, setTrim } from '../../../../../lib/common';
import { formRequiredRules } from '../../../../../lib/formRules';
import { REX_ENG_NAME, REX_GENERAL_PHONE, REX_URL } from '../../../../../lib/rex';
import './BrandDetailInfo.scss';

const BrandDetailInfo = (props) => {
  const {
    data,
    setData,
    form,
    isEditable,
    storeTagGroupList,
    permitRegistrationStoreTag,
    rejectRegistrationStoreTag,
    status,
  } = props;

  const {
    mainImg,
    subImg,
    storeName,
    storeEngName,
    tier,
    no,
    isExposureBrand,
    homepageUrl,
    naverStoreFarmUrl,
    address,
    storePhone,
    memo,
  } = data;

  return (
    <Panel name="brand-detail-info" className="brand-detail-info">
      <div className="panel-header">
        <div className="title">브랜드 정보</div>
      </div>

      <div className="panel-body">
        {/* 브랜드명 */}
        <InputForm
          label="브랜드명"
          form={form}
          isEditable={isEditable}
          rules={[formRequiredRules(true, '필수사항 입니다.')]}
          initialValue={storeName}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'storeName'],
              value: setTrim(value),
            })
          }
          name="storeName"
        />

        {/* 브랜드 영문명 */}
        <InputForm
          label="브랜드 영문명"
          form={form}
          isEditable={isEditable}
          rules={[
            {
              pattern: REX_ENG_NAME,
              message: '영문, 숫자, 특수문자만 입력 가능합니다.',
            },
          ]}
          initialValue={storeEngName}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'storeEngName'],
              value: setTrim(value),
            })
          }
          name="storeEngName"
        />

        {/*브랜드 컨셉 태그*/}
        <ConfirmTag
          label="브랜드 컨셉 태그"
          form={form}
          required={true}
          isEditable={isEditable}
          name="storeConceptTag"
          maxCnt={MAXIMUM_NUMBER_OF_STORE_CONCEPT_TAG}
          confirmTagData={data[TAG_GROUP_KIND.CONCEPT]}
          tagList={storeTagGroupList[TAG_GROUP_KIND.CONCEPT]}
          tagGroupType={TAG_GROUP_KIND.CONCEPT}
          isIndividualInspection={status !== STORE_STATUS.ACTIVATE}
          permitRegistrationTag={permitRegistrationStoreTag}
          rejectRegistrationTag={rejectRegistrationStoreTag}
          setData={setData}
          targetPath={['brandDetail', 'info', 'brandInfo', TAG_GROUP_KIND.CONCEPT]}
          subContent={
            <div className="sub-info">
              {`*브랜드 컨셉 태그는 최대 ${MAXIMUM_NUMBER_OF_STORE_CONCEPT_TAG}까지 등록이 가능합니다.`}
            </div>
          }
        />

        {/*브랜드 메인 이미지*/}
        <BaseForm
          label="브랜드 메인 이미지"
          content={
            !isEditable && !mainImg.length ? (
              <div>-</div>
            ) : (
              <ImgUpload
                list={mainImg}
                modalTitle="브랜드 메인 이미지"
                setData={setData}
                targetPath={['brandDetail', 'info', 'brandInfo', 'mainImg']}
                name="mainImg"
                isEditable={isEditable}
                cropMode={false}
              />
            )
          }
        />

        {/*브랜드 서브 이미지*/}
        <BaseForm
          label="브랜드 서브 이미지"
          content={
            !isEditable && !subImg.length ? (
              <div>-</div>
            ) : (
              <ImgUpload
                list={subImg}
                modalTitle="브랜드 서브 이미지"
                setData={setData}
                targetPath={['brandDetail', 'info', 'brandInfo', 'subImg']}
                name="subImg"
                isEditable={isEditable}
                cropMode={false}
              />
            )
          }
        />

        {/* 노출 여부 */}
        <BaseForm
          label="노출 여부"
          content={isExposureBrand ? <div>노출 중인 브랜드입니다.</div> : <div>비노출 중인 브랜드입니다.</div>}
        />

        {/*티어*/}
        <BaseForm label="티어" content={<div>{setDash(tier)}</div>} />

        {/*순서*/}
        <BaseForm label="티어 순서" content={<div>{setDash(no)}</div>} />

        {/* 홈페이지 */}
        <InputForm
          label="홈페이지"
          form={form}
          isEditable={isEditable}
          rules={[
            {
              pattern: REX_URL,
              message: '올바른 URL 을 입력하시기 바랍니다.',
            },
          ]}
          initialValue={homepageUrl}
          alterForm={<Anchor text={homepageUrl} color="primary" isUnderLine href={homepageUrl} target="_blank" />}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'homepageUrl'],
              value,
            })
          }
          name="homepageUrl"
        />

        {/* 네이버 스토어 */}
        <InputForm
          label="네이버 스토어"
          form={form}
          isEditable={isEditable}
          rules={[
            {
              pattern: REX_URL,
              message: '올바른 URL 을 입력하시기 바랍니다.',
            },
          ]}
          initialValue={naverStoreFarmUrl}
          alterForm={
            <Anchor text={naverStoreFarmUrl} color="primary" isUnderLine href={naverStoreFarmUrl} target="_blank" />
          }
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'naverStoreFarmUrl'],
              value,
            })
          }
          name="naverStoreFarmUrl"
        />

        {/* 오프라인 매장 주소 */}
        <BaseForm
          label="오프라인 매장 주소"
          content={
            <SearchAddress
              address={address}
              isEditable={isEditable}
              onSelect={(result) =>
                setData({
                  path: ['brandDetail', 'info', 'brandInfo', 'address'],
                  value: {
                    line2: address.line2,
                    ...result,
                  },
                })
              }
            />
          }
        />

        {/* 상세 주소 */}
        <InputForm
          className="address-line2"
          label="상세 주소"
          form={form}
          isEditable={isEditable}
          maxLength={MAIN_ADDRESS_LINE2_MAX_LENGTH}
          initialValue={address.line2}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'address', 'line2'],
              value: setTrim(value),
            })
          }
          name="line2"
        />

        {/* 대표번호 */}
        <InputForm
          label="대표번호"
          form={form}
          isEditable={isEditable}
          rules={[
            formRequiredRules(true, '필수사항 입니다.'),
            { pattern: REX_GENERAL_PHONE, message: '대표전화를 확인해주세요.' },
          ]}
          initialValue={setPhoneFormat(storePhone)}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'storePhone'],
              value: setClearDash(value),
            })
          }
          name="storePhone"
        />

        {/* 메모 */}
        <InputForm
          label="메모"
          form={form}
          isEditable={isEditable}
          initialValue={memo}
          onChange={(value) =>
            setData({
              path: ['brandDetail', 'info', 'brandInfo', 'memo'],
              value,
            })
          }
          name="memo"
        />
      </div>
    </Panel>
  );
};

export default BrandDetailInfo;
